import type { Middleware, MiddlewareAPI, AnyAction } from '@reduxjs/toolkit';

import { fetchTicket, buildSocketUrl } from '../../services/websocket';
import type { RootState } from './index';
import {
  wsConnect,
  wsDisconnect,
  wsSubscribe,
  wsUnsubscribe,
  wsStatusChanged,
  wsMessageReceived,
  WebSocketMessage,
} from './websocketSlice';

// Ping under API Gateway's 10-minute idle timeout so the socket stays open.
const PING_INTERVAL_MS = 5 * 60 * 1000;
const MAX_BACKOFF_MS = 30 * 1000;

export const websocketMiddleware: Middleware = (storeApi: MiddlewareAPI) => {
  let socket: WebSocket | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectAttempts = 0;
  let intentionalClose = false;

  const send = (payload: object): void => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(payload));
    }
  };

  const stopPing = (): void => {
    if (pingTimer) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  };

  const clearReconnect = (): void => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  // Indirection so the reconnect scheduler doesn't forward-reference `connect`.
  const connectRef: { current: () => Promise<void> } = { current: async () => {} };

  const scheduleReconnect = (): void => {
    clearReconnect();
    const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** reconnectAttempts);
    reconnectAttempts += 1;
    reconnectTimer = setTimeout(() => { void connectRef.current(); }, delay);
  };

  const connect = async (): Promise<void> => {
    intentionalClose = false;
    storeApi.dispatch(wsStatusChanged('connecting'));
    try {
      const ticket = await fetchTicket();
      socket = new WebSocket(buildSocketUrl(ticket.token));

      socket.onopen = () => {
        reconnectAttempts = 0;
        storeApi.dispatch(wsStatusChanged('connected'));
        // Replay resource-channel subscriptions (identity channels are re-assigned at $connect).
        const { channels } = (storeApi.getState() as RootState).websocket;
        channels.forEach((channel) => send({ action: 'subscribe', channel }));
        stopPing();
        pingTimer = setInterval(() => send({ action: 'ping' }), PING_INTERVAL_MS);
      };

      socket.onmessage = (event: MessageEvent) => {
        try {
          storeApi.dispatch(wsMessageReceived(JSON.parse(event.data) as WebSocketMessage));
        } catch {
          // Ignore non-JSON frames (e.g. keepalive acks).
        }
      };

      socket.onclose = () => {
        stopPing();
        socket = null;
        storeApi.dispatch(wsStatusChanged('disconnected'));
        if (!intentionalClose) scheduleReconnect();
      };

      socket.onerror = () => {
        socket?.close();
      };
    } catch {
      storeApi.dispatch(wsStatusChanged('disconnected'));
      if (!intentionalClose) scheduleReconnect();
    }
  };
  connectRef.current = connect;

  const disconnect = (): void => {
    intentionalClose = true;
    clearReconnect();
    stopPing();
    if (socket) {
      socket.close();
      socket = null;
    }
    storeApi.dispatch(wsStatusChanged('disconnected'));
  };

  return (next) => (action: AnyAction) => {
    // Let the slice update `channels` before we read/replay them.
    const result = next(action);

    if (wsConnect.match(action)) {
      void connect();
    } else if (wsDisconnect.match(action)) {
      disconnect();
    } else if (wsSubscribe.match(action)) {
      send({ action: 'subscribe', channel: action.payload });
    } else if (wsUnsubscribe.match(action)) {
      send({ action: 'unsubscribe', channel: action.payload });
    }

    return result;
  };
};
